import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { ArrowLeft, CreditCard, Globe, LayoutDashboard, ScrollText, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type NavItem, ShellFrame, SideNavLink } from "../components/shell-frame.js";
import { useShell } from "../components/shell-slots.js";

const CONSOLE_ITEMS: ReadonlyArray<NavItem> = [
  { to: "/admin", key: "admin.nav.overview", icon: LayoutDashboard, exact: true },
  { to: "/admin/users", key: "admin.nav.users", icon: Users },
  { to: "/admin/domains", key: "admin.nav.domains", icon: Globe },
  { to: "/admin/billing", key: "admin.nav.billing", icon: CreditCard },
  { to: "/admin/audit", key: "admin.nav.audit", icon: ScrollText },
];

/**
 * The admin console is its own surface, separate from the apps a user lives in, like Google
 * Admin. Only owners and administrators reach it (the route guard enforces that).
 */
export function AdminShell() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current =
    [...CONSOLE_ITEMS].reverse().find((item) => pathname.startsWith(item.to)) ?? CONSOLE_ITEMS[0];
  return (
    <ShellFrame
      title={current ? t(current.key) : t("admin.console")}
      brandSuffix={t("admin.brand")}
      homeTo="/admin"
      inConsole
      sideNav={<ConsoleNav />}
      banner={
        <div className="shrink-0 border-b border-rule px-6 py-1.5 text-xs text-ink-3">
          {t("admin.auditNote")}
        </div>
      }
    >
      <Outlet />
    </ShellFrame>
  );
}

function ConsoleNav() {
  const { t } = useTranslation();
  const { closeDrawer } = useShell();
  return (
    <nav aria-label={t("admin.console")} className="flex flex-col pt-1 pr-2 md:pr-3">
      {CONSOLE_ITEMS.map((item) => (
        <SideNavLink key={item.to} item={item} onNavigate={closeDrawer} />
      ))}
      <div className="mx-6 my-3 border-t border-rule" />
      <Link
        to="/mail"
        className="flex h-10 items-center gap-4 rounded-r-full pr-3 pl-6 text-sm text-ink-2 hover:bg-ink/5"
      >
        <ArrowLeft className="size-[18px] shrink-0" />
        {t("admin.backToMail")}
      </Link>
      <p className="px-6 pt-3 text-xs leading-relaxed text-ink-3">{t("admin.onlyAdmins")}</p>
    </nav>
  );
}
